<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const menuAberto = ref(false);
const gatilho = ref(null);

// O gatilho fica FORA do menu: sem o `ignore`, clicar nele com o menu aberto
// fechava pelo clique-fora e reabria pelo próprio clique — o menu só fechava
// clicando em outro lugar.
const fecharPorFora = [
  () => {
    menuAberto.value = false;
  },
  { ignore: [gatilho] },
];

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN;
});

const chatStatusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
    icon: 'i-lucide-message-circle',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
    icon: 'i-lucide-check-circle',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
    icon: 'i-lucide-clock',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
    icon: 'i-lucide-bell-off',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
    icon: 'i-lucide-layout-list',
  },
]);

const activeChatStatusIcon = computed(
  () =>
    chatStatusOptions.value.find(m => m.value === currentStatusFilter.value)
      ?.icon || 'i-lucide-message-circle'
);

// Só o STATUS mora aqui. A ordenação tinha um segundo seletor neste menu,
// com nove opções, enquanto a fileira "Ordenar por" logo abaixo — que aparece
// exatamente nas mesmas condições — oferecia cinco: dois controles para a
// mesma escolha, e um deles mostrando opções que o outro não sabia nomear.
const handleStatusChange = async value => {
  menuAberto.value = false;
  if (value === currentStatusFilter.value) return;
  try {
    await store.dispatch('setChatStatusFilter', value);
  } catch {
    showAlert(t('CHAT_LIST.CHAT_SORT.FAILED'));
    return;
  }
  emit('changeFilter', value, 'status');
  updateUISettings({
    conversations_filter_by: {
      status: value,
      order_by:
        chatSortFilter.value ||
        wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
    },
  });
};
</script>

<template>
  <div class="relative flex items-center">
    <button
      ref="gatilho"
      v-tooltip.right="$t('CHAT_LIST.CHAT_SORT.STATUS_TOOLTIP')"
      type="button"
      class="cbf-gatilho"
      :class="{ 'cbf-gatilho--aberto': menuAberto }"
      :aria-label="$t('CHAT_LIST.CHAT_SORT.STATUS_TOOLTIP')"
      aria-haspopup="menu"
      :aria-expanded="menuAberto"
      @click="menuAberto = !menuAberto"
    >
      <i :class="activeChatStatusIcon" class="cbf-gatilho__ico" />
      <i class="i-lucide-chevron-down cbf-gatilho__seta" />
    </button>

    <Transition name="advup-pop">
      <div
        v-if="menuAberto"
        v-on-click-outside="fecharPorFora"
        class="advup-menu advup-pop cbf-menu"
        :class="isOnExpandedLayout ? 'cbf-menu--fim' : 'cbf-menu--inicio'"
        role="menu"
        @keydown.esc="menuAberto = false"
      >
        <p class="advup-rotulo advup-rotulo--secao cbf-rot">
          {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
        </p>
        <button
          v-for="opt in chatStatusOptions"
          :key="opt.value"
          type="button"
          role="menuitemradio"
          class="advup-menu__item cbf-item"
          :class="{ 'cbf-item--on': currentStatusFilter === opt.value }"
          :aria-checked="currentStatusFilter === opt.value"
          @click="handleStatusChange(opt.value)"
        >
          <i :class="opt.icon" class="cbf-item__ico" />
          <span class="cbf-item__nome">{{ opt.label }}</span>
          <i
            v-if="currentStatusFilter === opt.value"
            class="i-lucide-check cbf-item__visto"
          />
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* O filtro de status da lista de conversas. A caixa e os itens são a peça do
   produto (`.advup-menu` + `.advup-pop`); aqui mora só o gatilho e o que é
   próprio de uma escolha única — o visto e o tom da escolhida. */

/* 28px e não 34: o gatilho mora no cabeçalho da coluna, ao lado dos botões
   `xs` da mesma fileira. */
.cbf-gatilho {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  height: 28px;
  padding: 0 var(--esp-2);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-2);
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition:
    border-color var(--mov-1) var(--mov-curva),
    background-color var(--mov-1) var(--mov-curva);
}

.cbf-gatilho:hover,
.cbf-gatilho--aberto {
  border-color: rgb(var(--blue-7));
  background: rgb(var(--blue-2));
}

.cbf-gatilho:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

.cbf-gatilho__ico {
  font-size: var(--ico-1);
  color: rgb(var(--blue-11));
}

/* A seta gira quando o menu abre: diz, sem palavra, que o clique de novo
   fecha. */
.cbf-gatilho__seta {
  font-size: var(--ico-1);
  color: rgb(var(--slate-9));
  transition: transform var(--mov-2) var(--mov-curva);
}

.cbf-gatilho--aberto .cbf-gatilho__seta {
  transform: rotate(180deg);
}

.cbf-menu {
  position: absolute;
  top: 100%;
  width: 220px;
  margin-top: var(--esp-2);
}

.cbf-menu--inicio {
  inset-inline-start: 0;
}

.cbf-menu--fim {
  inset-inline-end: 0;
}

.cbf-rot {
  margin: 0;
  padding: var(--esp-2) var(--esp-3) var(--esp-1);
}

.cbf-item__ico {
  flex-shrink: 0;
  font-size: var(--ico-2);
  color: rgb(var(--slate-10));
  transition: color var(--mov-1) var(--mov-curva);
}

.cbf-item__nome {
  flex: 1;
  min-width: 0;
}

.cbf-item__visto {
  flex-shrink: 0;
  font-size: var(--ico-2);
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.cbf-item:hover .cbf-item__ico,
.cbf-item--on .cbf-item__ico {
  color: rgb(var(--blue-11));
}

/* A escolhida em azul mesmo sem o cursor: aberto o menu, a pessoa precisa
   ver em qual status a lista está antes de mexer. */
.cbf-item--on {
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
}

@media (prefers-reduced-motion: reduce) {
  .cbf-gatilho__seta {
    transition: none;
  }

  .cbf-item__visto {
    animation: none;
  }
}

/* ── Celular ─────────────────────────────────────────────────────────────────
   28px de altura é o desenho da fileira compacta do desktop e está certo lá.
   O padrão pede 32px de alvo no toque; crescer a caixa quebraria a fileira,
   então a área cresce por ::after. */
@media (max-width: 639px) {
  .cbf-gatilho {
    position: relative;
  }

  .cbf-gatilho::after {
    content: '';
    position: absolute;
    inset: calc(-1 * var(--esp-1));
  }
}
</style>
